/* eslint-disable react-hooks/rules-of-hooks */
import { generateListOwnOemTicketsVariables } from "#/src/utils/filter/_openTicketQueryCond";
import {
  useLazyQuery,
  useQuery,
  type WatchQueryFetchPolicy,
} from "@apollo/client";

import {
  CHECK_SUPPORT_TICKET_HAS_STATUS_QUERY,
  GET_OWN_OEM_SUPPORT_TICKET_BY_ID_QUERY,
  LIST_CUSTOMERS_WITH_SUPPORT_TICKETS,
  LIST_OWN_OEM_KANBAN_SUPPORT_TICKETS_QUERY,
  LIST_OWN_SUPPORT_TICKET_MENTIONABLE_USERS_QUERY,
  LIST_SUPPORT_TICKET_ASSIGNABLE_USERS_QUERY,
  LIST_SUPPORT_TICKET_DE_ESCALATION_TARGETS_QUERY,
  TOTAL_SUPPORT_TICKETS_QUERY,
} from "~/api";
import { ITEMS_PER_PAGE } from "~/constants";
import {
  transformSupportTicket,
  transformSupportTickets,
} from "~/transformers/_supportTicketTransformer";
import {
  generateListOwnOemSupportTicketsVariables,
  generateSupportTicketQueryWhereCond,
} from "~/utils/filter/_supportTicketQueryCond";

interface KanbanArgs {
  activeFilters?: Record<string, unknown>;
  limit?: number;
  skip?: number;
  searchQuery?: string;
  sort?: string[];
  where?: Record<string, unknown>;
  skipCondition?: boolean;
}

/**
 * Kanban (per-status columns) variant of the support-ticket list. Each column's
 * cards are normalized to the shared view shape via `transformSupportTickets`.
 */
export const useListOwnOemKanbanSupportTickets = ({
  activeFilters,
  limit = ITEMS_PER_PAGE,
  skip = 0,
  searchQuery,
  sort,
  where,
  skipCondition = false,
}: KanbanArgs = {}) => {
  const variables = {
    params: {
      limit,
      skip,
      ...(sort ? { sort } : {}),
      where: {
        ...generateSupportTicketQueryWhereCond(activeFilters),
        ...(searchQuery ? { searchQuery } : {}),
        ...(where || {}),
      },
    },
  };

  const {
    data: { listOwnOemKanbanSupportTickets = {} } = {},
    loading,
    refetch: ticketsRefetch,
    fetchMore,
  } = useQuery(LIST_OWN_OEM_KANBAN_SUPPORT_TICKETS_QUERY, {
    variables,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
    skip: skipCondition,
  });

  const { totalCount, columns = [] } = listOwnOemKanbanSupportTickets;

  const transformedColumns = columns.map((column: any) => ({
    ...column,
    cards: transformSupportTickets(column?.cards || []),
  }));

  return {
    columns: transformedColumns,
    fetchMore,
    ticketsRefetch,
    totalCount,
    loading,
  };
};

export const useGetOwnOemSupportTicketById = (
  id?: string,
  skipCondition = false,
  fetchPolicy: WatchQueryFetchPolicy = "cache-and-network",
) => {
  const {
    data: { getOwnOemSupportTicketById } = {},
    loading,
    error,
    refetch,
  } = useQuery(GET_OWN_OEM_SUPPORT_TICKET_BY_ID_QUERY, {
    variables: { id },
    skip: !id || skipCondition,
    fetchPolicy,
  });

  return {
    ticket: transformSupportTicket(getOwnOemSupportTicketById),
    loading,
    error,
    refetch,
  };
};

/** Lazy single-ticket fetch used by the list cache reorder logic. */
export const useGetSupportTicketByIdForList = () => {
  const [getTicket] = useLazyQuery(GET_OWN_OEM_SUPPORT_TICKET_BY_ID_QUERY, {
    fetchPolicy: "cache-and-network",
  });

  return {
    getSupportTicketById: async (id: string) =>
      getTicket({ variables: { id } }),
  };
};

export const useListSupportTicketAssignableUsers = ({
  supportTicketId,
  skipCondition = false,
}: {
  supportTicketId?: string;
  skipCondition?: boolean;
}) => {
  const {
    data: { listSupportTicketAssignableUsers = [] } = {},
    loading,
    error,
  } = useQuery(LIST_SUPPORT_TICKET_ASSIGNABLE_USERS_QUERY, {
    variables: { supportTicketId },
    skip: !supportTicketId || skipCondition,
    // This list gates which members the assignee dropdown lets you pick, and it
    // moves whenever the ticket's teams do (asset/company writes evict it) or
    // whenever team membership changes elsewhere. Cache-first would keep
    // serving the stale set — showing the new team's members as disabled — so
    // re-validate on mount while still rendering the cached list immediately.
    fetchPolicy: "cache-and-network",
  });

  return {
    listAssignableUsers: listSupportTicketAssignableUsers,
    loading,
    error,
  };
};

/**
 * Portals an escalated ticket may be handed back down to.
 *
 * The list is the server's to compute (creator's ancestors still holding one of
 * the ticket's assets) — never derive it here. An empty result is a real
 * answer, not a failure: a root portal's own ticket has no ancestors, and the
 * caller is expected to say so rather than show an empty picker.
 *
 * `network-only`: the chain moves when assets are shared or reshared elsewhere,
 * and a de-escalation the caller just made changes who is eligible next time.
 * The picker mounts only when the modal opens, so this costs one round trip per
 * open.
 */
export const useListSupportTicketDeEscalationTargets = ({
  supportTicketId,
  skipCondition = false,
}: {
  supportTicketId?: string;
  skipCondition?: boolean;
}) => {
  const {
    data: { listSupportTicketDeEscalationTargets = [] } = {},
    loading,
    error,
  } = useQuery(LIST_SUPPORT_TICKET_DE_ESCALATION_TARGETS_QUERY, {
    variables: { input: { supportTicketId } },
    skip: !supportTicketId || skipCondition,
    fetchPolicy: "network-only",
  });

  return {
    targets: listSupportTicketDeEscalationTargets as Array<{
      _id: string;
      name: string;
    }>,
    loading,
    error,
  };
};

/**
 * Mentionable users (OEM + facility) for a support ticket's chat/comments
 * composer. Support-ticket counterpart of the WO `listTicketMentionableUsers`
 * service — hits the dedicated `listOwnSupportTicketMentionableUsers` endpoint.
 */
export const useListSupportTicketMentionableUsers = ({
  supportTicketId,
  skipCondition = false,
}: {
  supportTicketId?: string;
  skipCondition?: boolean;
}) => {
  const {
    data: {
      listOwnSupportTicketMentionableUsers = {
        facilityUsers: [],
        oemUsers: [],
      },
    } = {},
    loading,
    error,
  } = useQuery(LIST_OWN_SUPPORT_TICKET_MENTIONABLE_USERS_QUERY, {
    variables: { supportTicketId },
    skip: !supportTicketId || skipCondition,
  });

  return {
    mentionableUsers: listOwnSupportTicketMentionableUsers,
    loading,
    error,
  };
};

/**
 * Support-ticket counterpart of the work-order `useCheckTicketHasStatus`: tells
 * the status-customization settings whether a status still has tickets on it,
 * so deletion can offer a replacement status instead of deleting outright.
 */
export const useCheckSupportTicketHasStatus = () => {
  const [checkTickets, { loading, error }] = useLazyQuery(
    CHECK_SUPPORT_TICKET_HAS_STATUS_QUERY,
  );

  return {
    loading,
    error,
    checkIfStatusIsUsed: async (statusId: string): Promise<boolean> => {
      const { data: { checkSupportTicketHasStatus } = {}, error: checkError } =
        await checkTickets({
          variables: { statusId },
          fetchPolicy: "network-only",
        });

      // Treat a failed check as "in use": the caller deletes outright when this
      // returns false, so guessing wrong here would silently drop the status
      // from tickets that still carry it. A null result (null id) counts as a
      // failed check for the same reason.
      if (checkError || checkSupportTicketHasStatus == null) return true;

      return !!checkSupportTicketHasStatus;
    },
  };
};

export const useTotalSupportTickets = ({
  activeFilters,
  where,
  searchQuery,
  skipCondition = false,
}: {
  activeFilters?: Record<string, unknown>;
  where?: Record<string, unknown>;
  searchQuery?: string;
  skipCondition?: boolean;
} = {}) => {
  const variables = generateListOwnOemSupportTicketsVariables({
    activeFilters,
    where,
    searchQuery,
  });

  const { data: { totalSupportTickets = 0 } = {}, loading } = useQuery(
    TOTAL_SUPPORT_TICKETS_QUERY,
    {
      variables,
      fetchPolicy: "cache-and-network",
      skip: skipCondition,
    },
  );

  return { totalCount: totalSupportTickets, loading };
};

export const useListAllActiveSupportTicketCustomers = ({
  activeFilters,
  searchQuery,
  where,
  skipCondition,
  sort = [],
  notifyOnNetworkStatusChange = true,
} = {}) => {
  const variables = generateListOwnOemTicketsVariables({
    activeFilters,
    searchQuery,
    where,
    sort,
  });

  const { data: { listCustomersWithSupportTickets = [] } = {}, loading } =
    useQuery(LIST_CUSTOMERS_WITH_SUPPORT_TICKETS, {
      variables,
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange,
      skip: skipCondition,
    });

  const activeCustomers = listCustomersWithSupportTickets;

  return {
    activeCustomers,
    loading,
  };
};
